import orderAddHelper from "../helper/order-add.helper";
import OrderAddCargoAddress, {
  OrderAddCargoAddressType,
} from "../../../add-new/common/types/order-add-cargo-address";
import OrderAddCargoCompany from "../types/order-add-cargo-company";
import OrderAddCargoCompanySelectOption from "../types/order-add-cargo-company-select-option";
import OrderAddDispatcher from "../types/order-add-dispatcher";
import OrderAddDispatcherSelectOption from "../types/order-add-dispatcher-select-option";
import OrderAddPassenger, {
  OrderAddPassengerAddress,
} from "../types/order-add-passenger";
import OrderAddPassengerSelectOption from "../types/order-add-passenger-select-option";
import OrderAddRouteAddress from "../types/order-add-route-address";
import OrderAddRouteAddressSelectOption from "../types/order-add-route-address-select-option";
import OrderAddRouteItem, {
  OrderAddRouteWaypoint,
  OrderAddRouteWaypointGroupWaypoint,
} from "../types/order-add-route-waypoint";
import OrderAddTaxiCorporation from "../types/order-add-taxi-corporation";
import OrderAddTaxiCorporationSelectOption from "../types/order-add-taxi-corporation-select-option";
import {
  ContractsValidCollectionResponseData,
  ContractsValidCollectionResponseRailyContract,
  ContractsValidCollectionResponseTaxiContract,
} from "../../../../../common/api/raily/contracts/valid-collection/contracts-valid-collection.response";
import CargoCompanyList, {
  CargoCompanyListItem,
} from "../../../../../common/services/cargo-company/cargo-company/list/cargo-company-list";
import CargoCompanyDispatcherList, {
  CargoCompanyDispatcherListItem,
} from "../../../../../common/services/cargo-company/dispatcher/list/cargo-company-dispatcher-list";
import CargoCompanyPassengerList, {
  CargoCompanyPassengerListItem,
  CargoCompanyPassengerListItemAddress,
} from "../../../../../common/services/cargo-company/passenger/list/cargo-company-passenger-list";
import CargoCompanyAddressList, {
  CargoCompanyAddressListItem,
} from "../../../../../common/services/cargo-company/address/list/cargo-company-address-list";
import OrderAddParams, {
  OrderAddParamsCargoOrderRide,
  OrderAddParamsRideSeqItem,
  OrderAddParamsRideWaypoint,
  OrderAddParamsRideWaypointGroup,
  OrderAddParamsRideWaypointMetaCargoItem,
  OrderAddParamsSequenceOrder,
} from "../../../../../common/services/order/add/order-add-params";
import RideSolvedParams, {
  RideSolvedParamsRailyRide,
  RideSolvedParamsRideSeqItem,
  RideSolvedParamsRideWaypoint,
  RideSolvedParamsRideWaypointGroup,
  RideSolvedParamsRideWaypointMetaSubjects,
} from "../../../../../common/services/ride/solved/ride-solved-params";
import ContractsValidCollection, {
  ContractsValidCollectionRailyContract,
  ContractsValidCollectionTaxiContract,
} from "../../../../../common/services/contracts/valid-collection/contracts-valid-collection";
import CargoAddressType from "../../../../../common/types/cargo-address-type";

const createCargoCompany = (
  responseCargoCompany: CargoCompanyListItem,
): OrderAddCargoCompany => {
  return {
    uuid: responseCargoCompany.uuid,
    displayName: responseCargoCompany.displayName,
    name: responseCargoCompany.name,
    headquarterAddressDisplayName: responseCargoCompany.address,
  };
};

const createCargoCompanies = (
  responseCargoCompanies: CargoCompanyListItem[],
): OrderAddCargoCompany[] => {
  return responseCargoCompanies.map(createCargoCompany);
};

const createCargoCompanySelectOption = (
  cargoCompany: OrderAddCargoCompany,
): OrderAddCargoCompanySelectOption => {
  return {
    label: cargoCompany.displayName,
    value: cargoCompany,
    idForTesting: `cargo-company-uuid-${cargoCompany.uuid}`,
  };
};

const createCargoCompaniesSelectOptions = (
  responseCargoCompanies: CargoCompanyList,
): OrderAddCargoCompanySelectOption[] => {
  const cargoCompanies = createCargoCompanies(responseCargoCompanies.data);

  return cargoCompanies.map(createCargoCompanySelectOption);
};

const createTaxiCorporation = (
  responseTaxiCorporation: ContractsValidCollectionTaxiContract,
): OrderAddTaxiCorporation => {
  return {
    uuid: responseTaxiCorporation.taxiCorporation.id,
    displayName: responseTaxiCorporation.taxiCorporation.displayName,
    contractPermitsTollRoads: responseTaxiCorporation.allowChargeDuringRide,
    billingModel: responseTaxiCorporation.billingModel,
    contractUuid: responseTaxiCorporation.id,
  };
};

const createTaxiCorporations = (
  responseTaxiCorporations: ContractsValidCollectionTaxiContract[],
): OrderAddTaxiCorporation[] => {
  return responseTaxiCorporations.map(createTaxiCorporation);
};

const createTaxiCorporationSelectOption = (
  taxiCorporation: OrderAddTaxiCorporation,
): OrderAddTaxiCorporationSelectOption => {
  return {
    label: `${taxiCorporation.displayName} - ${taxiCorporation.billingModel}`,
    value: taxiCorporation,
    idForTesting: `taxi-uuid-${taxiCorporation.uuid}`,
  };
};

const createRailyTaxiCorporation = (
  railyContract: ContractsValidCollectionRailyContract,
): OrderAddTaxiCorporation => {
  return {
    uuid: railyContract.cargoCompanyId,
    displayName: "Raily",
    contractPermitsTollRoads: railyContract.allowChargeDuringRide,
    billingModel: undefined,
    contractUuid: railyContract.id,
  };
};

const createRailyTaxiCorporations = (
  railyContracts: ContractsValidCollectionRailyContract[],
): OrderAddTaxiCorporation[] => {
  return railyContracts.map(createRailyTaxiCorporation);
};

const createTaxiCorporationsSelectOptions = (
  taxiContractsResponseData: ContractsValidCollection,
): OrderAddTaxiCorporationSelectOption[] => {
  const allTaxiCorporations: OrderAddTaxiCorporation[] = [];

  const railyTaxiCorporation = createRailyTaxiCorporations(
    taxiContractsResponseData.railyContracts,
  );

  allTaxiCorporations.push(...railyTaxiCorporation);

  const taxiCorporations = createTaxiCorporations(
    taxiContractsResponseData.taxiContracts,
  );

  allTaxiCorporations.push(...taxiCorporations);

  return allTaxiCorporations.map(createTaxiCorporationSelectOption);
};

const createDispatcher = (
  responseDispatcher: CargoCompanyDispatcherListItem,
): OrderAddDispatcher => {
  return {
    uuid: responseDispatcher.uuid,
    displayName: responseDispatcher.displayName,
  };
};

const createDispatchers = (
  responseDispatchers: CargoCompanyDispatcherListItem[],
): OrderAddDispatcher[] => {
  return responseDispatchers.map(createDispatcher);
};

const createDispatcherSelectOption = (
  dispatcher: OrderAddDispatcher,
): OrderAddDispatcherSelectOption => {
  return {
    label: dispatcher.displayName,
    value: dispatcher,
    idForTesting: `dispatcher-uuid-${dispatcher.uuid}`,
  };
};

const createDispatcherSelectOptions = (
  responseDispatchers: CargoCompanyDispatcherList,
): OrderAddDispatcherSelectOption[] => {
  const dispatchers = createDispatchers(responseDispatchers);

  return dispatchers.map(createDispatcherSelectOption);
};

const createPassengerAddress = (
  responsePassengerAddress: CargoCompanyPassengerListItemAddress,
): OrderAddPassengerAddress => {
  return {
    uuid: responsePassengerAddress.id,
    displayName: responsePassengerAddress.displayName,
    apartmentNumber: responsePassengerAddress.apartmentNumber ?? "",
    houseNumber: responsePassengerAddress.houseNumber ?? "",
    street: responsePassengerAddress.street ?? "",
    town: responsePassengerAddress.town ?? "",
    zipCode: responsePassengerAddress.zipCode ?? "",
    latitude: responsePassengerAddress.lat,
    longitude: responsePassengerAddress.lon,
  };
};

const createPassengerAddresses = (
  responsePassengerAddresses: CargoCompanyPassengerListItemAddress[],
): OrderAddPassengerAddress[] => {
  return responsePassengerAddresses.map(createPassengerAddress);
};

const createPassenger = (
  responseItem: CargoCompanyPassengerListItem,
): OrderAddPassenger => {
  return {
    uuid: responseItem.user.id,
    firstName: responseItem.user.firstName,
    lastName: responseItem.user.lastName,
    addresses: createPassengerAddresses(responseItem.addresses),
  };
};

const createPassengers = (
  responsePassengers: CargoCompanyPassengerListItem[],
): OrderAddPassenger[] => {
  return responsePassengers.map(createPassenger);
};

const createPassengerSelectOption = (
  passenger: OrderAddPassenger,
): OrderAddPassengerSelectOption => {
  return {
    label: `${passenger.firstName} ${passenger.lastName}`,
    value: passenger,
    idForTesting: `passenger-uuid-${passenger.uuid}`,
  };
};

const createPassengerSelectOptions = (
  responsePassengers: CargoCompanyPassengerList,
): OrderAddPassengerSelectOption[] => {
  const passengers = createPassengers(responsePassengers);

  return passengers.map(createPassengerSelectOption);
};

const createType = (type: CargoAddressType): OrderAddCargoAddressType => {
  switch (type) {
    case CargoAddressType.HOTEL:
      return OrderAddCargoAddressType.HOTEL;
    case CargoAddressType.MEETING_POINT:
      return OrderAddCargoAddressType.MEETING_POINT;
    case CargoAddressType.STATION:
      return OrderAddCargoAddressType.STATION;
  }
};

const createCargoAddress = (
  responseCargoAddress: CargoCompanyAddressListItem,
): OrderAddCargoAddress => {
  return {
    uuid: responseCargoAddress.id,
    displayName: responseCargoAddress.displayName,
    apartmentNumber: responseCargoAddress.apartment ?? "",
    houseNumber: responseCargoAddress.houseNumber ?? "",
    street: responseCargoAddress.street ?? "",
    town: responseCargoAddress.town ?? "",
    zipCode: responseCargoAddress.zipCode ?? "",
    latitude: responseCargoAddress.lat,
    longitude: responseCargoAddress.lon,
    type: createType(responseCargoAddress.cargoAddressType),
    description: responseCargoAddress.description ?? null,
  };
};

const createCargoAddresses = (
  responseCargoAddresses: CargoCompanyAddressList,
): OrderAddCargoAddress[] => {
  return responseCargoAddresses.map(createCargoAddress);
};

const createRouteAddressSelectOption = (
  routeAddress: OrderAddRouteAddress,
): OrderAddRouteAddressSelectOption => {
  return {
    label: routeAddress.subText || routeAddress.displayName,
    value: routeAddress,
    idForTesting: `route-address-uuid-${routeAddress.uuid}`,
  };
};

const createRouteAddressesSelectOptions = (
  routeAddresses: OrderAddRouteAddress[],
): OrderAddRouteAddressSelectOption[] => {
  return routeAddresses.map(createRouteAddressSelectOption);
};

const createRailyRideWaypointFromRouteWaypoint = (
  routeWaypoint: OrderAddRouteWaypoint,
): OrderAddParamsRideWaypoint => {
  const newWaypoint: OrderAddParamsRideWaypoint = {
    lat: routeWaypoint.address!.latitude,
    lon: routeWaypoint.address!.longitude,
    displayName: routeWaypoint.address!.displayName,
    haltingTime: routeWaypoint.haltingTimeMinutes
      ? routeWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      uuid: routeWaypoint.uuid,
      cargoEnter: {
        subjects: routeWaypoint.onboardingPassengerListItems.map(
          (
            onboardingPassengerListItem,
          ): OrderAddParamsRideWaypointMetaCargoItem => {
            const passengerUuid = onboardingPassengerListItem.passenger.uuid;

            return {
              uuid: passengerUuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeWaypoint.outboardingPassengerListItems.map(
          (
            outboardingPassengerListItem,
          ): OrderAddParamsRideWaypointMetaCargoItem => {
            const passengerUuid = outboardingPassengerListItem.passenger.uuid;

            return {
              uuid: passengerUuid,
            };
          },
        ),
      },
    },
    time: routeWaypoint.date ? routeWaypoint.date?.toJSON() : undefined,
  };

  return newWaypoint;
};

const createRailyRideWaypointFromRouteGroupWaypoint = (
  routeGroupWaypoint: OrderAddRouteWaypointGroupWaypoint,
): OrderAddParamsRideWaypoint => {
  const newWaypoint: OrderAddParamsRideWaypoint = {
    lat: routeGroupWaypoint.address!.latitude,
    lon: routeGroupWaypoint.address!.longitude,
    displayName: routeGroupWaypoint.address!.displayName,
    haltingTime: routeGroupWaypoint.haltingTimeMinutes
      ? routeGroupWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      uuid: routeGroupWaypoint.uuid,
      cargoEnter: {
        subjects: routeGroupWaypoint.onboardingPassengerListItems.map(
          (
            onboardingPassengerListItem,
          ): OrderAddParamsRideWaypointMetaCargoItem => {
            return {
              uuid: onboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeGroupWaypoint.outboardingPassengerListItems.map(
          (
            outboardingPassengerListItem,
          ): OrderAddParamsRideWaypointMetaCargoItem => {
            return {
              uuid: outboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
    },
    time: undefined,
  };

  return newWaypoint;
};
const createSolvedOrderRailyRideWaypointFromRouteWaypoint = (
  routeWaypoint: OrderAddRouteWaypoint,
): RideSolvedParamsRideWaypoint => {
  const newWaypoint: RideSolvedParamsRideWaypoint = {
    lat: routeWaypoint.address!.latitude,
    lon: routeWaypoint.address!.longitude,
    displayName: routeWaypoint.address!.displayName,
    haltingTime: routeWaypoint.haltingTimeMinutes
      ? routeWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      id: routeWaypoint.uuid,
      cargoEnter: {
        subjects: routeWaypoint.onboardingPassengerListItems.map(
          (
            onboardingPassengerListItem,
          ): RideSolvedParamsRideWaypointMetaSubjects => {
            const passengerUuid = onboardingPassengerListItem.passenger.uuid;

            return {
              id: passengerUuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeWaypoint.outboardingPassengerListItems.map(
          (
            outboardingPassengerListItem,
          ): RideSolvedParamsRideWaypointMetaSubjects => {
            const passengerUuid = outboardingPassengerListItem.passenger.uuid;

            return {
              id: passengerUuid,
            };
          },
        ),
      },
    },
    time: routeWaypoint.date,
  };

  return newWaypoint;
};

const createSolvedOrderRailyRideWaypointFromRouteGroupWaypoint = (
  routeGroupWaypoint: OrderAddRouteWaypointGroupWaypoint,
): RideSolvedParamsRideWaypoint => {
  const newWaypoint: RideSolvedParamsRideWaypoint = {
    lat: routeGroupWaypoint.address!.latitude,
    lon: routeGroupWaypoint.address!.longitude,
    displayName: routeGroupWaypoint.address!.displayName,
    haltingTime: routeGroupWaypoint.haltingTimeMinutes
      ? routeGroupWaypoint.haltingTimeMinutes * 60
      : 0,
    meta: {
      id: routeGroupWaypoint.uuid,
      cargoEnter: {
        subjects: routeGroupWaypoint.onboardingPassengerListItems.map(
          (
            onboardingPassengerListItem,
          ): RideSolvedParamsRideWaypointMetaSubjects => {
            return {
              id: onboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
      cargoExit: {
        subjects: routeGroupWaypoint.outboardingPassengerListItems.map(
          (
            outboardingPassengerListItem,
          ): RideSolvedParamsRideWaypointMetaSubjects => {
            return {
              id: outboardingPassengerListItem.passenger.uuid,
            };
          },
        ),
      },
    },
    time: null,
  };

  return newWaypoint;
};

const createRailyRide = (
  routeItems: OrderAddRouteItem[],
): OrderAddParamsCargoOrderRide => {
  const mainSequence: OrderAddParamsRideSeqItem[] = [];

  routeItems.forEach((routeItem) => {
    const isWaypoint = orderAddHelper.checkIsRouteItemAWaypoint(routeItem);

    if (isWaypoint) {
      const newWaypoint: OrderAddParamsRideWaypoint =
        createRailyRideWaypointFromRouteWaypoint(routeItem);

      mainSequence.push(newWaypoint);

      return;
    }

    const sequence: OrderAddParamsRideWaypoint[] = [];

    routeItem.waypoints.forEach((waypoint) => {
      const newWaypoint: OrderAddParamsRideWaypoint =
        createRailyRideWaypointFromRouteGroupWaypoint(waypoint);

      sequence.push(newWaypoint);
    });

    const newCargoOrderRide: OrderAddParamsRideWaypointGroup = {
      sequenceOrder: OrderAddParamsSequenceOrder.ANY,
      seq: sequence,
    };

    mainSequence.push(newCargoOrderRide);
  });

  return {
    seq: mainSequence,
    sequenceOrder: OrderAddParamsSequenceOrder.STRICT,
  };
};

const createSolvedOrderRailyRide = (
  routeItems: OrderAddRouteItem[],
): RideSolvedParamsRailyRide => {
  const mainSequence: RideSolvedParamsRideSeqItem[] = [];

  routeItems.forEach((routeItem) => {
    const isWaypoint = orderAddHelper.checkIsRouteItemAWaypoint(routeItem);

    if (isWaypoint) {
      const newWaypoint: RideSolvedParamsRideWaypoint =
        createSolvedOrderRailyRideWaypointFromRouteWaypoint(routeItem);

      mainSequence.push(newWaypoint);

      return;
    }

    const sequence: RideSolvedParamsRideWaypoint[] = [];

    routeItem.waypoints.forEach((waypoint) => {
      const newWaypoint: RideSolvedParamsRideWaypoint =
        createSolvedOrderRailyRideWaypointFromRouteGroupWaypoint(waypoint);

      sequence.push(newWaypoint);
    });

    const newCargoOrderRide: RideSolvedParamsRideWaypointGroup = {
      sequenceOrder: "ANY",
      seq: sequence,
    };

    mainSequence.push(newCargoOrderRide);
  });

  return {
    seq: mainSequence,
    sequenceOrder: "STRICT",
  };
};

const createAddRequestParams = (
  orderNumber: string,
  selectedTaxiCorporation: OrderAddTaxiCorporation,
  routeItems: OrderAddRouteItem[],
  selectedDispatcherUuid: string | undefined,
): OrderAddParams => {
  const railyRide = createRailyRide(routeItems);
  const targetTaxiId =
    selectedTaxiCorporation!.displayName === "Raily"
      ? null
      : selectedTaxiCorporation!.uuid;

  return {
    externalId: orderNumber || null,
    targetTaxiUuid: targetTaxiId,
    railyRide: railyRide,
    cargoTaxiContractUuid: selectedTaxiCorporation.contractUuid || null,
    assumeDispatcherUuid: selectedDispatcherUuid,
  };
};

const createSolvedRidesRequest = (
  selectedTaxiCorporation: OrderAddTaxiCorporation,
  routeItems: OrderAddRouteItem[],
): RideSolvedParams => {
  const railyRide = createSolvedOrderRailyRide(routeItems);

  const nodeUuids = routeItems
    .map((routeItem) => {
      if (orderAddHelper.checkIsRouteItemAWaypoint(routeItem)) {
        return routeItem.uuid;
      }

      return routeItem.waypoints.map((waypoint) => waypoint.uuid);
    })
    .flat();

  return {
    railyRide: railyRide,
    nodeExclusionCollection: {
      exclusions: [
        {
          excludeApproaching: false,
          excludeInside: !selectedTaxiCorporation!.contractPermitsTollRoads,
          excludeReturning: false,
          nodeIds: nodeUuids,
        },
      ],
    },
  };
};

const orderAddFactory = {
  createCargoCompaniesSelectOptions,
  createTaxiCorporationsSelectOptions,
  createDispatcherSelectOptions,
  createPassengerSelectOptions,
  createCargoAddresses,
  createRouteAddressSelectOption,
  createRouteAddressesSelectOptions,
  createAddRequestParams,
  createSolvedRidesRequest,
};

export default orderAddFactory;
